import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { useSelector } from 'react-redux';

import "react-datepicker/dist/react-datepicker.css";

import { activityService } from '../../Services';

import Loader from '../Common/Loader';

import {ReactComponent as LoadingIcon} from '../../images/icons/General/loading-01.svg';

const parseDate = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-');
    return new Date(+y, m - 1, +d);
};

const toDateStr = (date) =>
    date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';

const parseTime = (str) => {
    if (!str) return null;
    const [h, m] = String(str).split(':');
    const d = new Date();
    d.setHours(+h, +m, 0, 0);
    return d;
};

const toTimeStr = (date) =>
    date ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}` : '';

const emptyForm = {
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    personInCharge: '',
    location: '',
    cost: '',
    maxSignups: '',
};

export const ActivityForm = ({ editMode }) => {
    const params = useParams();
    const navigate = useNavigate();

    const [info, setInfo] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const local = useSelector((state) => state.org.remember);
    const is24h = useSelector((state) => state[local ? 'localUser' : 'user'].is24h);

    useEffect(() => {
        if (editMode) {
            activityService.activityEditData(params.id).then(res => {
                if (res.error) {
                    setError(res.error);
                } else {
                    const a = res.activity;
                    setInfo({ id: a.activityTypeId, label: a.label, hasLocation: a.hasLocation, hasCost: a.hasCost });
                    setForm({
                        name: a.name || '',
                        description: a.description || '',
                        startDate: a.startDate || '',
                        endDate: a.endDate || '',
                        startTime: a.startTime || '',
                        endTime: a.endTime || '',
                        personInCharge: a.personInCharge || '',
                        location: a.location || '',
                        cost: a.cost ?? '',
                        maxSignups: a.maxSignups ?? '',
                    });
                }
            });
        } else {
            activityService.activityTypeInfo(params.slug).then(res => {
                if (res.error) {
                    setError(res.error);
                } else {
                    setInfo(res.info);
                }
            });
        }
    }, [params.slug, params.id, editMode]);

    const change = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        let res;
        if (editMode) {
            res = await activityService.activityEdit(params.id, form);
        } else {
            res = await activityService.activityAdd({ ...form, activityTypeId: info.id });
        }

        if (res.error) {
            setError(res.error);
            setLoading(false);
        } else {
            navigate(`/activities/${params.slug || info?.slug}/${editMode ? params.id : res.id}`);
        }
    };

    if (!info && !error) return <Loader />;

    return (
        <>
            <h1>{editMode ? 'Edit' : 'Add'} {info?.label}</h1>

            {error && <p className="error">{error}</p>}

            {info && (
                <form onSubmit={handleSubmit}>
                    <div className="form-column">
                        <label>Title</label>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={change}
                            required
                        />
                    </div>

                    <div className="form-column">
                        <label>Description<span>(Optional)</span></label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={change}
                            rows={4}
                        />
                    </div>

                    <div className="form-column">
                        <label>Start date</label>
                        <DatePicker
                            selected={form.startDate ? parseDate(form.startDate) : null}
                            onChange={(date) => {
                                const endDate = form.endDate ? parseDate(form.endDate) : null;
                                setForm({
                                    ...form,
                                    startDate: toDateStr(date),
                                    endDate: (endDate && date && endDate < date) ? '' : form.endDate,
                                });
                            }}
                            placeholderText="Select date"
                            dateFormat="MM/dd/yyyy"
                            {...(!editMode && { minDate: new Date() })}
                            required
                        />
                    </div>

                    <div className="form-column">
                        <label>End date<span>(Optional)</span></label>
                        <DatePicker
                            selected={form.endDate ? parseDate(form.endDate) : null}
                            onChange={(date) => setForm({ ...form, endDate: toDateStr(date) })}
                            placeholderText="Select date"
                            dateFormat="MM/dd/yyyy"
                            minDate={form.startDate ? parseDate(form.startDate) : (!editMode ? new Date() : null)}
                            isClearable
                        />
                    </div>

                    <div className="form-column">
                        <label>Start time<span>(Optional)</span></label>
                        <DatePicker
                            selected={form.startTime ? parseTime(form.startTime) : null}
                            onChange={(date) => setForm({ ...form, startTime: toTimeStr(date) })}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat={is24h > 0 ? 'HH:mm' : 'hh:mm a'} 
                            placeholderText="Select time"
                            isClearable
                        />
                    </div>

                    <div className="form-column">
                        <label>End time<span>(Optional)</span></label>
                        <DatePicker
                            selected={form.endTime ? parseTime(form.endTime) : null}
                            onChange={(date) => setForm({ ...form, endTime: toTimeStr(date) })}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat={is24h > 0 ? 'HH:mm' : 'hh:mm a'} 
                            placeholderText="Select time"
                            isClearable
                        />
                    </div>



                    <div className="form-column">
                        <label>Person in charge</label>
                        <input
                            type="text"
                            name="personInCharge"
                            value={form.personInCharge}
                            onChange={change}
                            required
                        />
                    </div>

                    {info.hasLocation ? (
                        <div className="form-column">
                            <label>Location</label>
                            <input
                                type="text"
                                name="location"
                                value={form.location}
                                onChange={change}
                                required
                            />
                        </div>
                    ) : null}

                    {info.hasCost ? (
                        <div className="form-column">
                            <label>Cost</label>
                            <input
                                type="number"
                                name="cost"
                                value={form.cost}
                                onChange={change}
                                min="0"
                                step="0.01"
                                required
                            />
                        </div>
                    ) : null}

                    <div className="form-column">
                        <label>Maximum signups</label>
                        <input
                            type="number"
                            name="maxSignups"
                            value={form.maxSignups}
                            onChange={change}
                            min="0"
                            step="1"
                            required
                        />
                    </div>

                    <div className="form-submit">
                        {error && <p className="error">{error}</p>}
                        <button type="submit" className="primary" disabled={loading}>
                            {loading ? <LoadingIcon className="btn-loader" /> : ''}
                            Save
                        </button>
                    </div>
                </form>
            )}
        </>
    );
};

export default ActivityForm;
